import { loadIndex } from './api.js';
import { deepSearch } from './search.js';
import { chapterCache } from './storage.js';
import { highlightMatch } from './utils/dom.js';

function debounce(fn, wait) {
	let timer;
	return (...args) => {
		clearTimeout(timer);
		timer = setTimeout(() => fn(...args), wait);
	};
}

function buildCategoryOptions(select, nodes, depth) {
	nodes.forEach((node) => {
		const opt = document.createElement('option');
		opt.value = node.slug;
		opt.textContent = `${'— '.repeat(depth)}${node.name} (${node.count})`;
		select.appendChild(opt);
		if (node.children && node.children.length) {
			buildCategoryOptions(select, node.children, depth + 1);
		}
	});
}

function renderHint(container, text) {
	container.textContent = '';
	const hint = document.createElement('p');
	hint.className = 'blb-cs-empty';
	hint.textContent = text;
	container.appendChild(hint);
}

function renderPostResults(container, items, i18n, query) {
	container.textContent = '';

	if (!items.length) {
		renderHint(container, i18n.noResults);
		return;
	}

	items.forEach((item) => {
		const card = document.createElement('article');
		card.className = 'blb-cs-result';

		const title = document.createElement('a');
		title.className = 'blb-cs-result-title';
		title.href = item.permalink;
		title.innerHTML = highlightMatch(item.title || '', query);
		card.appendChild(title);

		if (item.categories && item.categories.length) {
			const cats = document.createElement('div');
			cats.className = 'blb-cs-result-cats';
			cats.textContent = item.categories.map((c) => c.name).join(' • ');
			card.appendChild(cats);
		}

		if (item.excerpt) {
			const excerpt = document.createElement('p');
			excerpt.className = 'blb-cs-result-excerpt';
			excerpt.innerHTML = highlightMatch(item.excerpt, query);
			card.appendChild(excerpt);
		}

		if (item.date) {
			const date = document.createElement('time');
			date.className = 'blb-cs-result-date';
			date.textContent = item.date;
			card.appendChild(date);
		}

		container.appendChild(card);
	});
}

function verseLink(biblePage, verse, translation) {
	const url = new URL(biblePage, window.location.href);
	url.searchParams.set('blb_book', verse.book);
	url.searchParams.set('blb_chapter', String(verse.chapter));
	url.searchParams.set('blb_verse', String(verse.verse));
	url.searchParams.set('blb_translation', translation);
	return url.toString();
}

function renderVerseResults(container, verses, query, biblePage, translation) {
	container.textContent = '';

	verses.forEach((v) => {
		const card = document.createElement('a');
		card.className = 'blb-cs-verse';
		card.href = verseLink(biblePage, v, translation);

		const ref = document.createElement('div');
		ref.className = 'blb-cs-verse-ref';
		ref.textContent = `${v.bookName} ${v.chapter}:${v.verse}`;
		card.appendChild(ref);

		const text = document.createElement('div');
		text.className = 'blb-cs-verse-text';
		text.innerHTML = highlightMatch(v.text, query);
		card.appendChild(text);

		container.appendChild(card);
	});
}

function mount(root) {
	const config = window.BLB_CATEGORY_SEARCH || {};
	const restUrl = config.restUrl || '';
	const dataUrl = config.dataUrl || '';
	const translation = config.translation || 'kjv';
	const i18n = Object.assign(
		{
			hint: 'Start typing to search…',
			loading: 'Searching…',
			noResults: 'No posts found.',
			allCategories: 'All categories',
			postHeading: 'Posts',
			verseHeading: 'Bible Verses',
			noVerses: 'No verse matches found.',
			searching: 'Searching entire Bible…',
		},
		config.i18n || {}
	);

	const perPage = parseInt(root.dataset.perPage, 10) || 10;
	const initialCategory = root.dataset.category || '';
	const showFilter = root.dataset.showFilter !== '0';
	const placeholder = root.dataset.placeholder || '';
	const biblePage = root.dataset.biblePage || '';
	const includeScripture = root.dataset.includeScripture === '1' && !!biblePage && !!dataUrl;

	const form = document.createElement('div');
	form.className = 'blb-cs-form';

	const input = document.createElement('input');
	input.type = 'search';
	input.className = 'blb-cs-input';
	input.placeholder = placeholder;
	form.appendChild(input);

	let select = null;
	if (showFilter) {
		select = document.createElement('select');
		select.className = 'blb-cs-select';
		const allOpt = document.createElement('option');
		allOpt.value = '';
		allOpt.textContent = i18n.allCategories;
		select.appendChild(allOpt);
		form.appendChild(select);
	}

	const status = document.createElement('div');
	status.className = 'blb-cs-status';
	status.setAttribute('aria-live', 'polite');
	status.hidden = true;

	const postHeading = document.createElement('h4');
	postHeading.className = 'blb-cs-heading';
	postHeading.textContent = i18n.postHeading;
	postHeading.hidden = true;

	const posts = document.createElement('div');
	posts.className = 'blb-cs-results';

	let verseHeading = null;
	let verseStatus = null;
	let verses = null;

	if (includeScripture) {
		verseHeading = document.createElement('h4');
		verseHeading.className = 'blb-cs-heading';
		verseHeading.textContent = `${i18n.verseHeading} (${translation.toUpperCase()})`;
		verseHeading.hidden = true;

		verseStatus = document.createElement('div');
		verseStatus.className = 'blb-cs-status';
		verseStatus.setAttribute('aria-live', 'polite');
		verseStatus.hidden = true;

		verses = document.createElement('div');
		verses.className = 'blb-cs-results blb-cs-verses';
	}

	root.textContent = '';
	root.appendChild(form);
	root.appendChild(status);
	root.appendChild(postHeading);
	root.appendChild(posts);
	if (includeScripture) {
		root.appendChild(verseHeading);
		root.appendChild(verseStatus);
		root.appendChild(verses);
	}

	let currentPage = 1;
	let currentQuery = '';
	let currentCategory = initialCategory;
	let requestToken = 0;
	let deepCancel = null;

	function hasCriteria() {
		return currentQuery.trim() !== '' || currentCategory !== '';
	}

	function idle() {
		postHeading.hidden = true;
		renderHint(posts, i18n.hint);
		if (verseHeading) verseHeading.hidden = true;
		if (verseStatus) {
			verseStatus.hidden = true;
			verseStatus.textContent = '';
		}
		if (verses) verses.textContent = '';
		status.hidden = true;
		status.textContent = '';
	}

	function runPostSearch(token) {
		status.hidden = false;
		status.textContent = i18n.loading;

		const params = new URLSearchParams({
			q: currentQuery,
			category: currentCategory,
			per_page: String(perPage),
			page: String(currentPage),
		});

		fetch(`${restUrl}/search-posts?${params.toString()}`)
			.then((res) => res.json())
			.then((data) => {
				if (token !== requestToken) return;
				status.hidden = true;
				status.textContent = '';
				postHeading.hidden = false;
				renderPostResults(posts, data.results || [], i18n, currentQuery);
			})
			.catch(() => {
				if (token !== requestToken) return;
				status.hidden = true;
				status.textContent = '';
				postHeading.hidden = false;
				renderPostResults(posts, [], i18n, currentQuery);
			});
	}

	async function runVerseSearch(token) {
		if (!includeScripture) return;

		if (deepCancel) {
			deepCancel();
			deepCancel = null;
		}

		if (!currentQuery.trim()) {
			verseHeading.hidden = true;
			verseStatus.hidden = true;
			verseStatus.textContent = '';
			verses.textContent = '';
			return;
		}

		const index = await loadIndex(dataUrl);
		if (token !== requestToken || !index) return;

		verseHeading.hidden = false;
		verseStatus.hidden = false;
		verseStatus.textContent = i18n.searching;
		verses.textContent = '';

		const seen = new Set();
		const found = [];

		deepCancel = deepSearch(dataUrl, index, translation, currentQuery, config.offlineCache !== false, {
			onMatch(match) {
				if (token !== requestToken) return;
				const key = `${match.book}-${match.chapter}-${match.verse}`;
				if (seen.has(key)) return;
				seen.add(key);
				found.push(match);
				renderVerseResults(verses, found, currentQuery, biblePage, translation);
			},
			onProgress(scanned, total) {
				if (token !== requestToken) return;
				const pct = total ? Math.round((scanned / total) * 100) : 0;
				verseStatus.textContent = `${i18n.searching} ${pct}%`;
			},
			onDone() {
				if (token !== requestToken) return;
				deepCancel = null;
				verseStatus.hidden = true;
				verseStatus.textContent = '';
				if (!found.length) {
					renderHint(verses, i18n.noVerses);
				}
			},
		});
	}

	function runSearch() {
		const token = ++requestToken;
		runPostSearch(token);
		runVerseSearch(token);
	}

	input.addEventListener(
		'input',
		debounce((e) => {
			currentQuery = e.target.value.trim();
			currentPage = 1;
			if (hasCriteria()) {
				runSearch();
			} else {
				idle();
			}
		}, 300)
	);

	if (select) {
		select.addEventListener('change', (e) => {
			currentCategory = e.target.value;
			currentPage = 1;
			runSearch();
		});

		fetch(`${restUrl}/categories`)
			.then((res) => res.json())
			.then((tree) => {
				buildCategoryOptions(select, tree || [], 0);
				if (initialCategory) select.value = initialCategory;
			})
			.catch(() => {});
	}

	if (includeScripture) {
		chapterCache.ensureVersion(config.dataVersion || 1);
	}

	idle();
}

function boot() {
	document.querySelectorAll('.blb-category-search').forEach((node) => {
		if (node.dataset.blbCsMounted) return;
		node.dataset.blbCsMounted = '1';
		mount(node);
	});
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', boot);
} else {
	boot();
}
